export type JavaTokenKind =
  | "plain"
  | "keyword"
  | "type"
  | "function"
  | "string"
  | "number"
  | "comment"
  | "annotation"
  | "literal"
  | "operator";

export interface JavaToken {
  kind: JavaTokenKind;
  text: string;
}

export interface JavaCodeLine {
  number: number;
  tokens: JavaToken[];
}

const KEYWORDS = new Set([
  "abstract",
  "assert",
  "boolean",
  "break",
  "byte",
  "case",
  "catch",
  "char",
  "class",
  "const",
  "continue",
  "default",
  "do",
  "double",
  "else",
  "enum",
  "exports",
  "extends",
  "final",
  "finally",
  "float",
  "for",
  "goto",
  "if",
  "implements",
  "import",
  "instanceof",
  "int",
  "interface",
  "long",
  "module",
  "native",
  "new",
  "non-sealed",
  "open",
  "opens",
  "package",
  "permits",
  "private",
  "protected",
  "provides",
  "public",
  "record",
  "requires",
  "return",
  "sealed",
  "short",
  "static",
  "strictfp",
  "super",
  "switch",
  "synchronized",
  "this",
  "throw",
  "throws",
  "to",
  "transient",
  "transitive",
  "try",
  "uses",
  "var",
  "void",
  "volatile",
  "when",
  "while",
  "with",
  "yield",
]);

const LITERALS = new Set(["true", "false", "null"]);
const OPERATORS = new Set(
  "+-*/%=!<>&|^~?:;,.(){}[]".split(""),
);

function isIdentifierStart(character: string) {
  return /[A-Za-z_$]/.test(character);
}

function isIdentifierPart(character: string) {
  return /[A-Za-z0-9_$]/.test(character);
}

function nextNonSpace(source: string, index: number) {
  while (index < source.length && /[ \t\r]/.test(source[index])) index += 1;
  return source[index] ?? "";
}

export function highlightJava(source: string): JavaCodeLine[] {
  const lines: JavaToken[][] = [[]];
  const emit = (kind: JavaTokenKind, text: string) => {
    const parts = text.split("\n");
    for (let index = 0; index < parts.length; index += 1) {
      if (parts[index]) {
        const current = lines.at(-1)!;
        const previous = current.at(-1);
        if (previous?.kind === kind) previous.text += parts[index];
        else current.push({ kind, text: parts[index] });
      }
      if (index < parts.length - 1) lines.push([]);
    }
  };

  let cursor = 0;
  while (cursor < source.length) {
    const character = source[cursor];
    const pair = source.slice(cursor, cursor + 2);
    const triple = source.slice(cursor, cursor + 3);

    if (character === "\n") {
      emit("plain", "\n");
      cursor += 1;
      continue;
    }

    if (pair === "//") {
      const end = source.indexOf("\n", cursor);
      const stop = end < 0 ? source.length : end;
      emit("comment", source.slice(cursor, stop));
      cursor = stop;
      continue;
    }

    if (pair === "/*") {
      const end = source.indexOf("*/", cursor + 2);
      const stop = end < 0 ? source.length : end + 2;
      emit("comment", source.slice(cursor, stop));
      cursor = stop;
      continue;
    }

    if (triple === '"""') {
      const end = source.indexOf('"""', cursor + 3);
      const stop = end < 0 ? source.length : end + 3;
      emit("string", source.slice(cursor, stop));
      cursor = stop;
      continue;
    }

    if (character === '"' || character === "'") {
      const quote = character;
      let end = cursor + 1;
      let escaped = false;
      while (end < source.length) {
        const current = source[end];
        if (!escaped && current === quote) {
          end += 1;
          break;
        }
        if (!escaped && current === "\n") break;
        escaped = !escaped && current === "\\";
        if (current !== "\\") escaped = false;
        end += 1;
      }
      emit("string", source.slice(cursor, end));
      cursor = end;
      continue;
    }

    if (character === "@" && isIdentifierStart(source[cursor + 1] ?? "")) {
      let end = cursor + 2;
      while (
        end < source.length &&
        (isIdentifierPart(source[end]) || source[end] === ".")
      ) {
        end += 1;
      }
      emit("annotation", source.slice(cursor, end));
      cursor = end;
      continue;
    }

    if (
      /\d/.test(character) ||
      (character === "." && /\d/.test(source[cursor + 1] ?? ""))
    ) {
      let end = cursor + 1;
      while (
        end < source.length &&
        /[0-9A-Fa-f_xXbBpPeE.+\-LlFfDd]/.test(source[end])
      ) {
        end += 1;
      }
      emit("number", source.slice(cursor, end));
      cursor = end;
      continue;
    }

    if (isIdentifierStart(character)) {
      let end = cursor + 1;
      while (end < source.length && isIdentifierPart(source[end])) end += 1;
      const word = source.slice(cursor, end);
      const kind: JavaTokenKind = KEYWORDS.has(word)
        ? "keyword"
        : LITERALS.has(word)
          ? "literal"
          : /^[A-Z]/.test(word)
            ? "type"
            : nextNonSpace(source, end) === "("
              ? "function"
              : "plain";
      emit(kind, word);
      cursor = end;
      continue;
    }

    if (OPERATORS.has(character)) {
      let end = cursor + 1;
      while (
        end < source.length &&
        OPERATORS.has(source[end]) &&
        end - cursor < 3
      ) {
        end += 1;
      }
      emit("operator", source.slice(cursor, end));
      cursor = end;
      continue;
    }

    let end = cursor + 1;
    while (
      end < source.length &&
      source[end] !== "\n" &&
      !isIdentifierStart(source[end]) &&
      !/\d/.test(source[end]) &&
      !OPERATORS.has(source[end]) &&
      source.slice(end, end + 2) !== "//" &&
      source.slice(end, end + 2) !== "/*" &&
      source[end] !== '"' &&
      source[end] !== "'" &&
      source[end] !== "@"
    ) {
      end += 1;
    }
    emit("plain", source.slice(cursor, end));
    cursor = end;
  }

  return lines.map((tokens, index) => ({ number: index + 1, tokens }));
}
